import { Link } from '@tanstack/react-router';
import { ExternalLink, House } from 'lucide-react';
import { useId, type ReactNode, type RefObject } from 'react';
import { isSectionActive, type AdminMenuView, type AdminSection } from '../../admin-sections';
import type { AdminMenuCounts } from '../../use-admin-menu-counts';
import { AdminFind } from './AdminFind';
import { MenuCount } from './MenuCount';
import { MENU_ITEM_ATTRIBUTE, moveMenuFocus } from './roving-focus';
import styles from './AdminMenu.module.css';

interface AdminMenuProps {
  readonly menu: AdminMenuView;
  readonly counts: AdminMenuCounts;
  readonly pathname: string;
  /** `panel`: the 248px menu flush to the left edge. `sheet`: the same menu inside the phone's sheet. */
  readonly variant: 'panel' | 'sheet';
  readonly findRef?: RefObject<HTMLInputElement | null>;
  readonly autoFocusFind?: boolean;
  /** The sheet closes once somewhere is chosen. */
  readonly onNavigate?: () => void;
}

/**
 * Admin's grouped menu (Admin redesign 2026-11-02, Option A): "Find a setting" and Overview fixed at the top, the
 * groups below in a body that scrolls on its own. Real anchors, so every item opens in a new tab with Ctrl-click
 * and the back button works (06-qol §8).
 */
export function AdminMenu({ menu, counts, pathname, variant, findRef, autoFocusFind = false, onNavigate }: AdminMenuProps): ReactNode {
  const idPrefix = useId();
  const items = [...(menu.overview ? [menu.overview] : []), ...menu.groups.flatMap((group) => group.sections)];
  // One item in the tab order: the open page's, or the first. Arrow keys move between the rest.
  const tabStop = items.find((section) => section.isExternal !== true && isSectionActive(pathname, section)) ?? items[0];

  const item = (section: AdminSection, icon?: ReactNode): ReactNode => (
    <MenuLink
      section={section}
      icon={icon}
      count={counts[section.id]}
      isActive={isSectionActive(pathname, section)}
      isTabStop={section === tabStop}
      onNavigate={onNavigate}
    />
  );

  return (
    <nav
      className={variant === 'sheet' ? `${styles.menu} ${styles.sheet}` : styles.menu}
      aria-label="Admin"
      onKeyDown={(event) => {
        moveMenuFocus(event);
      }}
    >
      <div className={styles.top}>
        <AdminFind menu={menu} isInline={variant === 'sheet'} inputRef={findRef} autoFocus={autoFocusFind} onOpened={onNavigate} />
        {menu.overview && item(menu.overview, <House size={15} aria-hidden />)}
      </div>

      <div className={styles.body}>
        {menu.groups.map((group) => {
          const headId = `${idPrefix}-${group.id}`;
          return (
            <div key={group.id} className={styles.group}>
              <div id={headId} className={styles.groupLabel}>
                {group.label}
              </div>
              <ul className={styles.list} aria-labelledby={headId}>
                {group.sections.map((section) => (
                  <li key={section.id}>{item(section)}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </nav>
  );
}

interface MenuLinkProps {
  readonly section: AdminSection;
  readonly icon?: ReactNode;
  readonly count: number | undefined;
  readonly isActive: boolean;
  readonly isTabStop: boolean;
  readonly onNavigate?: () => void;
}

function MenuLink({ section, icon, count, isActive, isTabStop, onNavigate }: MenuLinkProps): ReactNode {
  const className = section.sublabel !== undefined ? `${styles.item} ${styles.two}` : styles.item;
  const focus = { [MENU_ITEM_ATTRIBUTE]: '', tabIndex: isTabStop ? 0 : -1 };
  const text = (
    <span className={styles.text}>
      <span className={styles.label}>{section.label}</span>
      {section.sublabel !== undefined && <span className={styles.sublabel}>{section.sublabel}</span>}
    </span>
  );
  const countLabel = count !== undefined && section.countLabel ? section.countLabel(count) : '';

  if (section.isExternal === true) {
    // Bull Board is a different application (S8), so it opens beside this one rather than replacing it.
    return (
      <a href={section.to} className={className} target="_blank" rel="noreferrer" onClick={onNavigate} {...focus}>
        {icon}
        {text}
        <ExternalLink size={13} aria-hidden className={styles.external} />
        <span className="sr-only">(opens in a new tab)</span>
      </a>
    );
  }

  return (
    <Link
      to={section.to}
      className={className}
      // Our rule decides what is open (People also covers its invite page), not the router's prefix match.
      activeOptions={{ exact: true }}
      activeProps={{ 'aria-current': isActive ? 'page' : undefined }}
      aria-current={isActive ? 'page' : undefined}
      onClick={onNavigate}
      {...focus}
    >
      {icon}
      {text}
      <MenuCount count={count} label={countLabel} />
    </Link>
  );
}
